/**
 * ShopPage — Full WooCommerce store page with filtering, cart, and wishlist.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/seo/SEOHead';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, Search, Star, Tag, RefreshCw, ShoppingCart, ArrowRight } from 'lucide-react';
import { ConfigurableHero } from '@/components/common/ConfigurableHero';
import { WishlistButton } from '@/components/common/WishlistButton';
import { usePageDesign } from '@/hooks/usePageDesign';
import { useWCProducts, useWCCategories } from '@/hooks/useWooCommerce';
import { useCart } from '@/hooks/useCart';
import { toast } from 'sonner';

const ShopPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const { heroStyle, showBreadcrumbs, showTrustBadges, showCtaButtons } = usePageDesign('products');

  const { data: result, isLoading } = useWCProducts({
    per_page: 50,
    search: search || undefined,
    category: categoryFilter !== 'all' ? Number(categoryFilter) : undefined,
  });

  const products = result?.data || [];
  const { data: categories = [] } = useWCCategories();
  const { addItem } = useCart();

  const filtered = products.filter(p => {
    if (typeFilter === 'subscription') return p.type?.includes('subscription');
    if (typeFilter === 'one-time') return !p.type?.includes('subscription');
    return true;
  });

  const handleAddToCart = (product: any) => {
    addItem({
      product_id: String(product.id),
      product_name: product.name,
      product_image: product.images?.[0]?.src || null,
      product_price: product.price ? parseFloat(product.price) : null,
      product_url: `/products/${product.slug}`,
      quantity: 1,
      variation_id: null,
      variation_attributes: null,
      is_subscription: product.type?.includes('subscription') || false,
      subscription_period: product.subscription_period || null,
      subscription_interval: product.subscription_period_interval ? parseInt(product.subscription_period_interval) : null,
    });
    toast.success(`${product.name} added to cart`);
  };

  return (
    <>
      <SEOHead title="Shop" description="Browse our full catalog of products and subscriptions." />
      <div className="min-h-screen">
        <ConfigurableHero
          heroStyle={heroStyle}
          title="Shop"
          subtitle="Browse our full catalog of premium products, one-time purchases, and subscription plans."
          badge="Store"
          showBreadcrumbs={showBreadcrumbs}
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Shop' }]}
          showTrustBadges={showTrustBadges}
          showCtaButtons={showCtaButtons}
        />

        {/* Filters */}
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
            <div className="relative w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-[180px]"><SelectValue placeholder="All Categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-[160px]"><SelectValue placeholder="All Types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="one-time">One-Time</SelectItem>
                  <SelectItem value="subscription">Subscriptions</SelectItem>
                </SelectContent>
              </Select>
              <Badge variant="secondary" className="whitespace-nowrap">{filtered.length} products</Badge>
            </div>
          </div>
        </div>

        {/* Product Grid */}
        <div className="max-w-6xl mx-auto px-4 pb-16">
          {isLoading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map(i => <div key={i} className="h-80 rounded-xl bg-muted animate-pulse" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-lg">No products found.</p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map(p => {
                const isSubscription = p.type?.includes('subscription');
                return (
                  <Link
                    key={p.id}
                    to={`/products/${p.slug}`}
                    className="group rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col"
                  >
                    <div className="aspect-[4/3] bg-muted relative overflow-hidden">
                      {p.images[0]?.src ? (
                        <img src={p.images[0].src} alt={p.images[0].alt || p.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center"><Package className="h-10 w-10 text-muted-foreground/30" /></div>
                      )}
                      <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                        {p.featured && <Badge className="bg-primary text-primary-foreground gap-1"><Star className="h-3 w-3 fill-current" />Featured</Badge>}
                        {p.on_sale && <Badge variant="destructive" className="gap-1"><Tag className="h-3 w-3" />Sale</Badge>}
                        {isSubscription && <Badge variant="secondary" className="gap-1"><RefreshCw className="h-3 w-3" />Subscription</Badge>}
                      </div>
                      <div className="absolute top-3 right-3">
                        <WishlistButton
                          productId={String(p.id)}
                          productName={p.name}
                          productImage={p.images[0]?.src}
                          productPrice={p.price ? parseFloat(p.price) : undefined}
                          productUrl={`/products/${p.slug}`}
                          className="bg-background/80 backdrop-blur-sm hover:bg-background"
                        />
                      </div>
                      {!p.in_stock && <Badge variant="destructive" className="absolute bottom-3 left-3">Out of Stock</Badge>}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors mb-1">{p.name}</h3>
                      {p.short_description && <p className="text-sm text-muted-foreground line-clamp-2 mb-3" dangerouslySetInnerHTML={{ __html: p.short_description }} />}
                      <div className="mt-auto space-y-3">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xl font-bold text-foreground">${parseFloat(p.price || '0').toLocaleString()}</span>
                          {p.on_sale && p.regular_price && <span className="text-sm text-muted-foreground line-through">${parseFloat(p.regular_price).toLocaleString()}</span>}
                          {isSubscription && p.subscription_period && <span className="text-xs text-primary">/{p.subscription_period}</span>}
                        </div>
                        <Button
                          size="sm"
                          className="w-full gap-2"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddToCart(p); }}
                          disabled={!p.in_stock}
                        >
                          <ShoppingCart className="h-3.5 w-3.5" />
                          {isSubscription ? 'Subscribe' : 'Add to Cart'}
                        </Button>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ShopPage;
